// src/services/sushiswapApi.js
/* global BigInt */
import { feeConfig, determineFeeChargeMethod } from '../config/feeConfig';
import secureFetch from '../utils/secureFetch';

/**
 * Service for interacting with SushiSwap API
 */

/**
 * Improve error message readability for user display
 * @param {string} errorMessage - Raw error message 
 * @param {function} t - Translation function from i18next
 * @returns {string} - User-friendly error message
 */
const getReadableErrorMessage = (errorMessage, t) => {
  // If no translation function is provided, use default English strings
  const translate = t || ((key) => {
    // Simple fallback implementation
    const fallbacks = {
      'swap.errors.insufficientAllowance': "Token needs approval first. Please approve the token.",
      'swap.errors.priceMoved': "Price moved too much. Try increasing slippage tolerance.",
      'swap.errors.priceMovedFavorably': "Price moved favorably. Try again.",
      'swap.errors.insufficientFunds': "Not enough funds for this transaction and gas fees.",
      'swap.errors.transferFailed': "Transfer failed. Try approving the token again.",
      'swap.errors.priceImpactTooHigh': "Price impact too high. Try a smaller amount.",
      'swap.errors.insufficientGas': "Not enough gas. Try increasing gas limit.",
      'swap.errors.networkError': "Network error. Check your connection and try again.",
      'swap.errors.userRejected': "Transaction rejected by user.",
      'swap.errors.networkRejected': "Transaction rejected by the network. Try again with different gas settings.",
      'swap.errors.transactionReverted': "Transaction reverted. Try adjusting slippage or amount.",
      'swap.errors.transferFailed': "Token transfer failed. Please try approving the token again."
    };
    return fallbacks[key] || key;
  });

  // Don't show SushiSwap specific errors to users
  const userFriendlyErrors = {
    "insufficient allowance": translate('swap.errors.insufficientAllowance'),
    "Insufficient token allowance": translate('swap.errors.insufficientAllowance'),
    "INSUFFICIENT_OUTPUT_AMOUNT": translate('swap.errors.priceMoved'),
    "Too little received": translate('swap.errors.priceMoved'),
    "EXCESSIVE_INPUT_AMOUNT": translate('swap.errors.priceMovedFavorably'),
    "insufficient funds": translate('swap.errors.insufficientFunds'),
    "ETH_BALANCE_TOO_LOW": translate('swap.errors.insufficientFunds'),
    "TRANSFER_FROM_FAILED": translate('swap.errors.transferFailed'),
    "price impact too high": translate('swap.errors.priceImpactTooHigh'),
    "gas required exceeds allowance": translate('swap.errors.insufficientGas'),
    "Failed to fetch": translate('swap.errors.networkError'),
    "User rejected": translate('swap.errors.userRejected'),
    "user denied": translate('swap.errors.userRejected'),
    "Transaction discarded": translate('swap.errors.networkRejected'),
    "execution reverted": translate('swap.errors.transactionReverted'),
    "SafeERC20: low-level call failed": translate('swap.errors.transferFailed')
  };

  // Look for known error patterns
  for (const [errorPattern, friendlyMessage] of Object.entries(userFriendlyErrors)) {
    if (errorMessage.toLowerCase().includes(errorPattern.toLowerCase())) {
      return friendlyMessage;
    }
  }

  // If no specific match, remove SushiSwap references and keep it concise
  let cleanedMessage = errorMessage
    .replace(/SushiSwap/g, "Exchange")
    .replace(/API/g, "service")
    .replace(/api/g, "service");

  // If message is too long, keep only first sentence or 80 chars
  if (cleanedMessage.length > 80) {
    const firstSentence = cleanedMessage.split('.')[0];
    if (firstSentence && firstSentence.length < 80) {
      return firstSentence + '.';
    }
    return cleanedMessage.substring(0, 80) + '...';
  }

  return cleanedMessage;
};

/**
 * Get swap quote using SushiSwap API (/quote endpoint)
 * This endpoint doesn't require approval or check for balance
 * 
 * @param {object} chainConfig - Chain configuration
 * @param {object} inputToken - Input token object
 * @param {object} outputToken - Output token object
 * @param {string} amountIn - Input amount (in normal decimal form)
 * @param {object} options - Additional options with t function for translations
 * @returns {Promise<object>} - Quote data with exchange rates
 */
export const fetchSushiSwapQuote = async (chainConfig, inputToken, outputToken, amountIn, options = {}) => {
  const t = options.t; // Translation function
  
  if (!chainConfig?.chainId) {
    throw new Error(t ? t('swap.errors.invalidChainConfig') : 'Chain configuration missing chainId');
  }

  // Validate input parameters
  if (!inputToken?.address) throw new Error(t ? t('swap.errors.invalidInputToken') : 'Invalid input token');
  if (!outputToken?.address) throw new Error(t ? t('swap.errors.invalidOutputToken') : 'Invalid output token');
  if (!amountIn || isNaN(parseFloat(amountIn)) || parseFloat(amountIn) <= 0) {
    throw new Error(t ? t('swap.errors.invalidInputAmount') : 'Invalid input amount');
  }

  // SushiSwap API endpoint for QUOTES
  const baseUrl = chainConfig.sushiswapConfig?.apiBaseUrl || 'https://api.sushi.com';
  
  // Format the amount with appropriate decimals
  let formattedAmountIn;
  try {
    // Import functions only when needed to avoid circular dependencies
    const { ethers } = await import('ethers');
    
    // CRITICAL FIX: Ensure decimals is a number, not a string
    const decimals = typeof inputToken.decimals === 'string' 
      ? parseInt(inputToken.decimals, 10) 
      : (inputToken.decimals || 18);
    
    // Also normalize the input amount
    const normalizedAmount = amountIn.toString().replace(',', '.');
    formattedAmountIn = ethers.parseUnits(normalizedAmount, decimals).toString();
    
    console.log("Parsing amount with decimals:", {
      amount: normalizedAmount,
      decimals: decimals,
      parsedResult: formattedAmountIn
    });
  } catch (error) {
    console.error("Error formatting input amount:", error);
    const errorMsg = t 
      ? t('swap.errors.formatInputAmountFailed', { message: error.message }) 
      : `Failed to format input amount: ${error.message}`;
    throw new Error(errorMsg);
  }

  // Build the SushiSwap QUOTE API URL with query parameters
  const chainId = chainConfig.chainId;
  const url = new URL(`${baseUrl}/quote/v6/${chainId}`);
  
  // Add required parameters
  url.searchParams.set('tokenIn', inputToken.address);
  url.searchParams.set('tokenOut', outputToken.address);
  url.searchParams.set('amount', formattedAmountIn);
  
  // Add slippage parameter - as a decimal value (e.g., 0.005 for 0.5%)
  if (options.slippage) {
    const slippageDecimal = parseFloat(options.slippage) / 100;
    url.searchParams.set('maxSlippage', slippageDecimal.toString());
  } else {
    // Default slippage of 0.5%
    url.searchParams.set('maxSlippage', '0.005');
  }
  
  // Add fee parameters if enabled, making sure to respect SushiSwap's constraints
  if (feeConfig.enabled) {
    // SushiSwap requires fee to be <= 0.003 (0.3%)
    const feeDecimal = Math.min(feeConfig.swapFeePercentage / 10000, 0.003);
    url.searchParams.set('fee', feeDecimal.toString());
    
    // Dynamically determine fee charge method
    const feeChargeMethod = determineFeeChargeMethod(inputToken, outputToken, chainConfig);
    
    // Convert feeBy from our format to SushiSwap format
    // SushiSwap expects 'input' or 'output', not 'currency_in' or 'currency_out'
    const feeBy = feeChargeMethod === 'currency_in' ? 'input' : 'output';
    url.searchParams.set('feeBy', feeBy);
    
    console.log(`Using fee method: ${feeChargeMethod} (${feeBy} for SushiSwap API)`);
  }
  
  // Add client ID for tracking if available
  if (options.clientId) {
    url.searchParams.set('referrer', options.clientId);
  } else if (feeConfig.feeReceiver) {
    // Use fee receiver as referrer if client ID not provided
    url.searchParams.set('referrer', feeConfig.feeReceiver);
  }
  
  // Add max price impact (0.99 = 99%)
  url.searchParams.set('maxPriceImpact', '0.99');
  
  // Add debug and visualization parameters
  url.searchParams.set('debug', 'true');
  url.searchParams.set('vizualize', 'true');

  console.log(`Fetching exchange quote:`, {
    url: url.toString(),
    inputToken: inputToken.symbol,
    outputToken: outputToken.symbol,
    amountIn: formattedAmountIn
  });

  try {
    const response = await secureFetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    // Get the raw response text for debugging
    const responseText = await response.text();
    console.log("API quote response:", responseText);
    
    // Try to parse JSON response
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error("Failed to parse API response:", responseText);
      throw new Error(t ? t('swap.errors.serviceError', { status: 'parse', message: '' }) : 'Failed to parse service response');
    }
    
    if (!response.ok) {
      // Handle different HTTP error codes
      if (response.status === 400) {
        throw new Error(t ? t('swap.errors.invalidParameters') : 'Invalid parameters: Check your input values');
      } else if (response.status === 429) {
        throw new Error(t ? t('swap.errors.rateLimitExceeded') : 'Rate limit exceeded. Please try again later.');
      } else {
        throw new Error(t 
          ? t('swap.errors.serviceError', { status: response.status, message: data?.error || '' }) 
          : `Service error (${response.status}): ${data?.error || responseText}`
        );
      }
    }
    
    // Validate successful response
    if (!data || data.status !== 'Success') {
      // Create user-friendly error messages for common issues
      if (data?.error?.includes("insufficient")) {
        throw new Error(t ? t('swap.errors.insufficientLiquidity') : 'Insufficient liquidity for this trade. Try a smaller amount.');
      } else if (data?.error?.includes("price impact")) {
        throw new Error(t ? t('swap.errors.priceImpactTooHigh') : 'Price impact too high. Try a smaller amount or increase max price impact.');
      } else {
        throw new Error(data?.error || (t ? t('swap.errors.serviceError', { status: '', message: '' }) : 'Service returned an error'));
      }
    }
  
    return data;
  } catch (error) {
    // Enhance error handling with more specific error categorization
    if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
      console.error('Network error connecting to exchange:', error);
      throw new Error(t ? t('swap.errors.networkError') : 'Network error. Please check your connection.');
    }
    
    console.error('Error fetching exchange quote:', error);
    throw new Error(getReadableErrorMessage(error.message, t));
  }
};

/**
 * Get swap route and transaction data using SushiSwap API
 * @param {object} chainConfig - Chain configuration
 * @param {object} inputToken - Input token object
 * @param {object} outputToken - Output token object
 * @param {string} amountIn - Input amount (in normal decimal form)
 * @param {object} options - Additional options with t function for translations
 * @returns {Promise<object>} - Swap route data with transaction information
 */
export const fetchSushiSwapRoute = async (chainConfig, inputToken, outputToken, amountIn, options = {}) => {
  const t = options.t; // Translation function
  
  if (!chainConfig?.chainId) {
    throw new Error(t ? t('swap.errors.invalidChainConfig') : 'Chain configuration missing chainId');
  }

  // Validate input parameters
  if (!inputToken?.address) throw new Error(t ? t('swap.errors.invalidInputToken') : 'Invalid input token');
  if (!outputToken?.address) throw new Error(t ? t('swap.errors.invalidOutputToken') : 'Invalid output token');
  if (!amountIn || isNaN(parseFloat(amountIn)) || parseFloat(amountIn) <= 0) {
    throw new Error(t ? t('swap.errors.invalidInputAmount') : 'Invalid input amount');
  }

  // CRITICAL: Validate userAddress is provided
  if (!options.userAddress) {
    console.error("userAddress not provided in fetchSushiSwapRoute options");
    throw new Error(t ? t('swap.errors.userAddressRequired') : 'User wallet address is required for exchange service');
  }

  // First check for a quote to determine if the trade is even possible
  // This helps separate "insufficient allowance" from "insufficient liquidity" issues
  try {
    // Get a quote first (doesn't require approval or balance)
    await fetchSushiSwapQuote(chainConfig, inputToken, outputToken, amountIn, options);
  } catch (quoteError) {
    // If there's an error with the quote (e.g., no liquidity), propagate it
    console.error("Error getting quote before swap:", quoteError);
    throw new Error(getReadableErrorMessage(quoteError.message, t));
  }

  // SushiSwap API endpoint for SWAP
  const baseUrl = chainConfig.sushiswapConfig?.apiBaseUrl || 'https://api.sushi.com';
  
  // Format the amount with appropriate decimals
  let formattedAmountIn;
  try {
    // Import functions only when needed to avoid circular dependencies
    const { ethers } = await import('ethers');
    
    // CRITICAL FIX: Ensure decimals is a number, not a string
    const decimals = typeof inputToken.decimals === 'string' 
      ? parseInt(inputToken.decimals, 10) 
      : (inputToken.decimals || 18);
    
    // Also normalize the input amount
    const normalizedAmount = amountIn.toString().replace(',', '.');
    formattedAmountIn = ethers.parseUnits(normalizedAmount, decimals).toString();
  } catch (error) {
    console.error("Error formatting input amount:", error);
    const errorMsg = t 
      ? t('swap.errors.formatInputAmountFailed', { message: error.message }) 
      : `Failed to format input amount: ${error.message}`;
    throw new Error(errorMsg);
  }

  // Build the SushiSwap SWAP API URL with query parameters
  const chainId = chainConfig.chainId;
  const url = new URL(`${baseUrl}/swap/v6/${chainId}`);
  
  // Add required parameters
  url.searchParams.set('tokenIn', inputToken.address);
  url.searchParams.set('tokenOut', outputToken.address);
  url.searchParams.set('amount', formattedAmountIn);
  url.searchParams.set('sender', options.userAddress);
  url.searchParams.set('validate', 'true'); // Validate the transaction
  
  // Add slippage parameter - as a decimal value (e.g., 0.005 for 0.5%)
  if (options.slippage) {
    const slippageDecimal = parseFloat(options.slippage) / 100;
    url.searchParams.set('maxSlippage', slippageDecimal.toString());
  } else {
    // Default slippage of 0.5%
    url.searchParams.set('maxSlippage', '0.005');
  }
  
  // Determine if we should try the dynamic fee first or just use default
  let useDynamicFee = true;
  
  // Detect special case: when the input is a wrapped token and output is not native
  if (isWrappedNativeToken(inputToken.address, chainConfig) && 
      !isNativeOrWrappedToken(outputToken.address, chainConfig)) {
    // This combination may cause issues with some pools, just use the default approach
    console.log("Using default fee collection method due to wrapped token input");
    useDynamicFee = false;
  }
  
  // First try: Use either dynamic or default fee config
  let feeBy = 'output'; // SushiSwap default
  
  if (feeConfig.enabled) {
    // SushiSwap requires fee to be <= 0.003 (0.3%)
    const feeDecimal = Math.min(feeConfig.swapFeePercentage / 10000, 0.003);
    url.searchParams.set('fee', feeDecimal.toString());
    
    if (useDynamicFee) {
      // Dynamically determine fee charge method
      const feeChargeMethod = determineFeeChargeMethod(inputToken, outputToken, chainConfig);
      
      // Convert feeBy from our format to SushiSwap format
      feeBy = feeChargeMethod === 'currency_in' ? 'input' : 'output';
    } else {
      // Use the default
      feeBy = feeConfig.chargeFeeBy === 'currency_in' ? 'input' : 'output';
    }
    
    url.searchParams.set('feeBy', feeBy);
    url.searchParams.set('feeReceiver', feeConfig.feeReceiver);
    
    console.log(`Using fee method: ${feeBy} for SushiSwap API`);
  }
  
  // Add client ID for tracking if available
  if (options.clientId) {
    url.searchParams.set('referrer', options.clientId);
  } else if (feeConfig.feeReceiver) {
    // Use fee receiver as referrer if client ID not provided
    url.searchParams.set('referrer', options.clientId || feeConfig.feeReceiver);
  }
  
  // Optional: add deadline if provided
  if (options.deadline) {
    const deadlineTimestamp = Math.floor(Date.now() / 1000) + (options.deadline * 60);
    url.searchParams.set('deadline', deadlineTimestamp.toString());
  }

  // Add max price impact (0.99 = 99%)
  url.searchParams.set('maxPriceImpact', '0.99');

  // Add debug and visualization parameters
  url.searchParams.set('debug', 'true');
  url.searchParams.set('vizualize', 'true');

  console.log(`Fetching exchange swap route:`, {
    url: url.toString(),
    inputToken: inputToken.symbol,
    outputToken: outputToken.symbol,
    amountIn: formattedAmountIn,
    userAddress: options.userAddress,
    feeBy
  });

  try {
    const result = await tryFetchSwapRoute(url, inputToken, outputToken, formattedAmountIn, chainConfig, options);
    return result;
  } catch (error) {
    // If it failed and we were using dynamic fee, try again with the opposite fee method
    if (useDynamicFee && feeConfig.enabled && error.message.includes('422')) {
      console.log("First attempt failed with dynamic fee method. Trying alternative fee method...");
      
      // Create a new URL with the opposite fee method
      const fallbackUrl = new URL(url.toString());
      const oppositeFeeBy = feeBy === 'input' ? 'output' : 'input';
      fallbackUrl.searchParams.set('feeBy', oppositeFeeBy);
      
      console.log(`Using fallback fee method: ${oppositeFeeBy}`);
      
      try {
        return await tryFetchSwapRoute(fallbackUrl, inputToken, outputToken, formattedAmountIn, chainConfig, options);
      } catch (fallbackError) {
        // Both attempts failed, try one more time with default SushiSwap fee settings
        console.log("Both fee methods failed. Trying with default SushiSwap settings...");
        
        // Create a new URL without our custom fee parameters
        const defaultUrl = new URL(`${baseUrl}/swap/v6/${chainId}`);
        
        // Add only required parameters
        defaultUrl.searchParams.set('tokenIn', inputToken.address);
        defaultUrl.searchParams.set('tokenOut', outputToken.address);
        defaultUrl.searchParams.set('amount', formattedAmountIn);
        defaultUrl.searchParams.set('sender', options.userAddress);
        defaultUrl.searchParams.set('validate', 'true');
        
        // Add slippage
        if (options.slippage) {
          const slippageDecimal = parseFloat(options.slippage) / 100;
          defaultUrl.searchParams.set('maxSlippage', slippageDecimal.toString());
        } else {
          defaultUrl.searchParams.set('maxSlippage', '0.005');
        }
        
        // Add deadline if provided
        if (options.deadline) {
          const deadlineTimestamp = Math.floor(Date.now() / 1000) + (options.deadline * 60);
          defaultUrl.searchParams.set('deadline', deadlineTimestamp.toString());
        }
        
        // Add other necessary parameters
        defaultUrl.searchParams.set('maxPriceImpact', '0.99');
        defaultUrl.searchParams.set('debug', 'true');
        defaultUrl.searchParams.set('vizualize', 'true');
        
        try {
          return await tryFetchSwapRoute(defaultUrl, inputToken, outputToken, formattedAmountIn, chainConfig, options);
        } catch (defaultError) {
          // All attempts failed, throw the original error
          console.error("All fee methods failed:", error, fallbackError, defaultError);
          throw error;
        }
      }
    }
    
    // Original error if not a fee method issue or if all attempts failed
    throw error;
  }
};

/**
 * Helper function to check if a token is the native token or wrapped native token
 * @param {string} tokenAddress - Token address to check
 * @param {object} chainConfig - Chain configuration
 * @returns {boolean} - True if token is native or wrapped native
 */
function isNativeOrWrappedToken(tokenAddress, chainConfig) {
  if (!tokenAddress) return false;
  
  const address = tokenAddress.toLowerCase();
  const nativeAddress = "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE".toLowerCase();
  const wrappedAddress = chainConfig?.wrappedNativeToken ? 
    chainConfig.wrappedNativeToken.toLowerCase() : '';
  
  return address === nativeAddress || (wrappedAddress && address === wrappedAddress);
}

/**
 * Helper function to check if a token is specifically the wrapped native token
 * @param {string} tokenAddress - Token address to check
 * @param {object} chainConfig - Chain configuration
 * @returns {boolean} - True if token is wrapped native
 */
function isWrappedNativeToken(tokenAddress, chainConfig) {
  if (!tokenAddress || !chainConfig?.wrappedNativeToken) return false;
  
  return tokenAddress.toLowerCase() === chainConfig.wrappedNativeToken.toLowerCase();
}

/**
 * Try to fetch swap route with the given URL
 * @param {URL} url - URL with all parameters
 * @param {object} inputToken - Input token object
 * @param {object} outputToken - Output token object
 * @param {string} formattedAmountIn - Formatted input amount
 * @param {object} chainConfig - Chain configuration
 * @param {object} options - Additional options with t function for translations
 * @returns {Promise<object>} - Swap route data
 */
async function tryFetchSwapRoute(url, inputToken, outputToken, formattedAmountIn, chainConfig, options) {
  const t = options.t; // Translation function
  
  try {
    const response = await secureFetch(url.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    // Get the raw response text for debugging
    const responseText = await response.text();
    console.log("API swap response:", responseText);
    
    // Try to parse JSON response
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error("Failed to parse API response:", responseText);
      throw new Error(t ? t('swap.errors.serviceError', { status: 'parse', message: '' }) : 'Failed to parse service response');
    }

    // Handle insufficient allowance error specifically
    if (data.type === "https://docs.sushi.com/api/errors/insufficient-allowance") {
      throw new Error(t ? t('swap.errors.insufficientAllowance') : "Insufficient token allowance. Please approve the token first.");
    }
    
    if (!response.ok) {
      // Handle different HTTP error codes
      if (response.status === 400) {
        throw new Error(t ? t('swap.errors.invalidParameters') : 'Invalid parameters: Check your input values');
      } else if (response.status === 429) {
        throw new Error(t ? t('swap.errors.rateLimitExceeded') : 'Rate limit exceeded. Please try again later.');
      } else {
        throw new Error(t 
          ? t('swap.errors.serviceError', { status: response.status, message: data?.error || '' }) 
          : `Service error (${response.status}): ${data?.error || ''}`
        );
      }
    }
    
    // Validate successful response
    if (!data || data.status !== 'Success') {
      // Create user-friendly error messages for common issues
      if (data?.error?.includes("insufficient")) {
        throw new Error(t ? t('swap.errors.insufficientLiquidity') : 'Insufficient liquidity for this trade. Try a smaller amount.');
      } else if (data?.error?.includes("price impact")) {
        throw new Error(t ? t('swap.errors.priceImpactTooHigh') : 'Price impact too high. Try a smaller amount or increase max price impact.');
      } else {
        throw new Error(data?.error || (t ? t('swap.errors.serviceError', { status: '', message: '' }) : 'Service returned an error'));
      }
    }
  
    // Check for required data fields
    if (!data.tokens || !data.assumedAmountOut || !data.tx) {
      console.error("API missing critical fields:", data);
      throw new Error(t ? t('swap.errors.serviceError', { status: 'missing', message: 'Missing critical data' }) : 'Missing critical data in service response');
    }
    
    // Format response to be compatible with our application
    const formattedResponse = {
      data: {
        // Add route summary for compatibility with existing code
        routeSummary: {
          tokenIn: inputToken.address,
          tokenOut: outputToken.address,
          amountIn: formattedAmountIn,
          amountOut: data.assumedAmountOut,
          sender: options.userAddress,
          recipient: options.userAddress,
          _tx: data.tx  // Store the transaction data for later use
        },
        // Add transaction data directly from response
        tx: data.tx,
        // Transaction router address
        routerAddress: data.tx.to,
        // Include amount out
        amountOut: data.assumedAmountOut,
        // Include price impact if available
        outputChange: {
          percent: data.priceImpact ? Math.abs(parseFloat(data.priceImpact) * 100) : 0.5
        }
      },
      // Raw response for reference
      _raw: data
    };
    
    return formattedResponse;
  } catch (error) {
    // Specifically handle insufficient allowance errors to inform the user they need to approve
    if (error.message && (
        error.message.includes("insufficient allowance") || 
        error.message.includes("Insufficient token allowance"))) {
      console.error("Insufficient allowance error:", error);
      throw new Error(t ? t('swap.errors.insufficientAllowance') : "Insufficient token allowance. Please approve the token first.");
    }
    
    // Enhance error handling with more specific error categorization
    if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
      console.error('Network error connecting to exchange:', error);
      throw new Error(t ? t('swap.errors.networkError') : 'Network error connecting to exchange. Please check your connection.');
    }
    
    console.error('Error fetching exchange route:', error);
    throw new Error(getReadableErrorMessage(error.message, t));
  }
}

/**
 * Check if a chain uses SushiSwap API for swaps
 * @param {object} chainConfig - Chain configuration object
 * @returns {boolean} - True if chain uses SushiSwap API
 */
export const isUsingSushiSwap = (chainConfig) => {
  return chainConfig?.aggregatorAddress === 'SushiSwap';
};

/**
 * Special function that simulates the KyberSwap encodeSwapData interface
 * but actually just returns the transaction data that's already encoded by SushiSwap API
 * 
 * This allows us to maintain compatibility with the existing code structure
 */
export const encodeSwapData = async (
  chainConfig, 
  routeSummary, 
  sender, 
  recipient, 
  slippageTolerance = 50, 
  deadline = 20,
  options = {}
) => {
  const t = options.t; // Translation function
  
  // We don't actually need to encode anything, as SushiSwap already provides the tx data
  // We just need to return it in a format that's compatible with the existing code
  
  // Check if routeSummary contains the necessary tx data
  if (!routeSummary || !routeSummary._tx) {
    throw new Error(t ? t('swap.errors.missingTransactionData') : 'Missing transaction data in route summary');
  }
  
  // Return the data in a format compatible with the existing code
  return {
    data: {
      data: routeSummary._tx.data,
      inputAmount: routeSummary._tx.value,
      gasLimit: routeSummary._tx.gas || "1000000" // Include gas limit if available
    },
    _metadata: {
      sender,
      recipient,
      deadline,
      slippageBps: slippageTolerance,
      protocol: 'Exchange'
    }
  };
};

/**
 * Get transaction parameters for a SushiSwap transaction
 * Helper function to prepare transaction parameters for sendTransaction
 * 
 * @param {object} routeSummary - Route summary from SushiSwap API
 * @param {object} options - Additional options with t function for translations
 * @returns {object} - Transaction parameters for sendTransaction
 */
export const getSushiSwapTransactionParams = (routeSummary, options = {}) => {
  const t = options.t; // Translation function
  
  if (!routeSummary || !routeSummary._tx) {
    throw new Error(t ? t('swap.errors.missingTransactionData') : 'Missing transaction data in route summary');
  }
  
  const txData = routeSummary._tx.data;
  if (!txData || txData === '0x' || txData === '') {
    throw new Error(t ? t('swap.errors.emptyTransactionData') : "Received empty transaction data from exchange");
  }
  
  // Prepare transaction parameters
  const txParams = {
    to: routeSummary._tx.to,
    data: txData,
    // DO NOT set 'from' as it's implicit with the wallet's sendTransaction
  };
  
  // IMPORTANT: Set value if present in the response (for native token swaps)
  if (routeSummary._tx.value) {
    // Ensure value is properly formatted as a BigInt
    txParams.value = BigInt(routeSummary._tx.value);
  } else {
    // Set to 0 for non-native token swaps
    txParams.value = 0n;
  }
  
  // Add gas limit from API or use a safe default
  if (routeSummary._tx.gas) {
    txParams.gasLimit = BigInt(routeSummary._tx.gas);
  } else {
    txParams.gasLimit = BigInt(1000000); // 1M gas as fallback
  }
  
  // Add gas price if provided in options
  if (options.gasPrice) {
    txParams.gasPrice = options.gasPrice;
  } else if (routeSummary._tx.gasPrice) {
    txParams.gasPrice = BigInt(routeSummary._tx.gasPrice);
  }
  
  return txParams;
};